/**
 * Mini-wrapper IndexedDB (promise) per la cache HTTP e la coda di scrittura offline.
 * Se IndexedDB non è disponibile (navigazione privata, errori di quota) ricade su una Map in memoria:
 * l'app funziona comunque, solo senza persistenza tra i riavvii.
 */
import { Capacitor } from "@capacitor/core";
import { kvGet, kvRemove, kvSet } from "./kv";

const DB_NAME = "ml-offline-db";
const DB_VERSION = 1;
export type StoreName = "http" | "queue" | "meta";

let dbPromise: Promise<IDBDatabase | null> | null = null;
const memory: Record<StoreName, Map<string, unknown>> = { http: new Map(), queue: new Map(), meta: new Map() };

/** Dove stanno davvero i dati: IndexedDB, file dell'app (ripiego nativo) o solo memoria (si perdono al riavvio). */
export type StorageMode = "idb" | "fs" | "memory";
let mode: StorageMode = "idb";
export const getStorageMode = (): StorageMode => mode;

// ---- ripiego su file (app nativa senza IndexedDB utilizzabile) -----------------------------------------
const fsIndexKey = (store: StoreName) => `idbfs_${store}__index`;
const fsKey = (store: StoreName, key: string) => `idbfs_${store}_${hashKey(key)}`;
function hashKey(k: string): string {
  let h1 = 5381;
  let h2 = 52711;
  for (let i = 0; i < k.length; i++) {
    const c = k.charCodeAt(i);
    h1 = (h1 * 33) ^ c;
    h2 = (h2 * 33) ^ c;
  }
  return `${(h1 >>> 0).toString(36)}${(h2 >>> 0).toString(36)}${k.length.toString(36)}`;
}
const fsIndexCache: Partial<Record<StoreName, Record<string, string>>> = {};
async function fsIndex(store: StoreName): Promise<Record<string, string>> {
  if (!fsIndexCache[store]) fsIndexCache[store] = (await kvGet<Record<string, string>>(fsIndexKey(store))) ?? {};
  return fsIndexCache[store] as Record<string, string>;
}

function open(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains("http")) db.createObjectStore("http", { keyPath: "k" });
        if (!db.objectStoreNames.contains("queue")) db.createObjectStore("queue", { keyPath: "id" });
        if (!db.objectStoreNames.contains("meta")) db.createObjectStore("meta", { keyPath: "k" });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  dbPromise = dbPromise.then((db) => {
    if (db) {
      mode = "idb";
      return db;
    }
    mode = Capacitor.isNativePlatform() ? "fs" : "memory";
    return null;
  });
  return dbPromise;
}

function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const keyField = (store: StoreName) => (store === "queue" ? "id" : "k");

export async function idbGet<T>(store: StoreName, key: string): Promise<T | undefined> {
  const db = await open();
  if (!db && mode === "fs") return ((await kvGet<T>(fsKey(store, key))) ?? undefined) as T | undefined;
  if (!db) return memory[store].get(key) as T | undefined;
  try {
    return (await wrap(db.transaction(store).objectStore(store).get(key))) as T | undefined;
  } catch {
    return memory[store].get(key) as T | undefined;
  }
}

export async function idbPut(store: StoreName, value: Record<string, unknown>): Promise<void> {
  const db = await open();
  const key = String(value[keyField(store)]);
  if (!db && mode === "fs") {
    const idx = await fsIndex(store);
    const fk = fsKey(store, key);
    await kvSet(fk, value);
    if (!idx[key]) {
      idx[key] = fk;
      await kvSet(fsIndexKey(store), idx);
    }
    return;
  }
  if (!db) {
    memory[store].set(key, value);
    return;
  }
  try {
    await wrap(db.transaction(store, "readwrite").objectStore(store).put(value));
  } catch {
    memory[store].set(key, value);
  }
}

export async function idbDelete(store: StoreName, key: string): Promise<void> {
  const db = await open();
  memory[store].delete(key);
  if (!db && mode === "fs") {
    const idx = await fsIndex(store);
    if (idx[key]) {
      await kvRemove(idx[key]);
      delete idx[key];
      await kvSet(fsIndexKey(store), idx);
    }
    return;
  }
  if (!db) return;
  try {
    await wrap(db.transaction(store, "readwrite").objectStore(store).delete(key));
  } catch {
    /* già assente */
  }
}

export async function idbAll<T>(store: StoreName): Promise<T[]> {
  const db = await open();
  if (!db && mode === "fs") {
    const idx = await fsIndex(store);
    const rows = await Promise.all(Object.values(idx).map((fk) => kvGet<T>(fk)));
    return rows.filter((r) => r != null) as T[];
  }
  if (!db) return Array.from(memory[store].values()) as T[];
  try {
    return (await wrap(db.transaction(store).objectStore(store).getAll())) as T[];
  } catch {
    return Array.from(memory[store].values()) as T[];
  }
}

export async function idbClear(store: StoreName): Promise<void> {
  const db = await open();
  memory[store].clear();
  if (!db && mode === "fs") {
    const idx = await fsIndex(store);
    await Promise.all(Object.values(idx).map((fk) => kvRemove(fk)));
    fsIndexCache[store] = {};
    await kvSet(fsIndexKey(store), {});
    return;
  }
  if (!db) return;
  try {
    await wrap(db.transaction(store, "readwrite").objectStore(store).clear());
  } catch {
    /* niente */
  }
}

export async function idbCount(store: StoreName): Promise<number> {
  const db = await open();
  if (!db && mode === "fs") return Object.keys(await fsIndex(store)).length;
  if (!db) return memory[store].size;
  try {
    return await wrap(db.transaction(store).objectStore(store).count());
  } catch {
    return memory[store].size;
  }
}

/** Verifica reale di scrittura/lettura: se fallisce IndexedDB passa al ripiego su file. */
export async function idbSelfTest(): Promise<StorageMode> {
  const db = await open();
  if (db) {
    try {
      await wrap(db.transaction("meta", "readwrite").objectStore("meta").put({ k: "selftest", t: Date.now() }));
      const back = await wrap(db.transaction("meta").objectStore("meta").get("selftest"));
      if (back) return "idb";
    } catch {
      /* passa al ripiego */
    }
    // IndexedDB aperto ma inutilizzabile
    dbPromise = Promise.resolve(null);
    mode = Capacitor.isNativePlatform() ? "fs" : "memory";
  }
  return mode;
}
